import { CSSProperties } from '@ant-design/cssinjs/lib/hooks/useStyleRegister';
import { AnyAction, Dispatch } from '@reduxjs/toolkit';
import { parseInt } from 'common/helpers';
import {
  ElemTypes,
  IElement,
  IStickyPoints,
  IStylesByElemId,
} from 'common/interfaces';
import { ResizeHandlerPositions } from 'hooks/hooks.interfaces';
import { changeEverySelectedElemByOwnStyles } from 'store/reducers/elems.slice';

import { ResizeService } from './ResizeService';

export class SelectionResizeService extends ResizeService {
  public initialSelectionStyles: CSSProperties;
  public initialGroupElems: IElement[];

  constructor(
    public selectedElems: IElement[],
    domElem: HTMLElement,
    setElemSize: (
      value: React.SetStateAction<{
        width: number;
        height: number;
      }>,
    ) => void,
    pressedKeys: string[],
    readonly stickyPoints: IStickyPoints[],
    public readonly dispatch: Dispatch<AnyAction>,
  ) {
    super(domElem, setElemSize, pressedKeys, stickyPoints, true);

    this.initialSelectionStyles = {
      left: domElem.style.left,
      top: domElem.style.top,
      width: domElem.style.width,
      height: domElem.style.height,
    };
    this.initialGroupElems = selectedElems.filter(
      (item) => item.elemType === ElemTypes.GROUP,
    );
    this.dispatch = dispatch;
  }

  _transformChildren(
    children: IElement[],
    deltaX: number,
    deltaY: number,
  ): IStylesByElemId[] {
    const transformed: IStylesByElemId[] = [];

    for (const item of children) {
      transformed.push({
        id: item.id,
        styles: {
          left: parseInt(item.styles.left) * (deltaX + 1) + 'px',
          top: parseInt(item.styles.top) * (deltaY + 1) + 'px',
          width: parseInt(item.styles.width) * (1 + deltaX) + 'px',
          height: parseInt(item.styles.height) * (1 + deltaY) + 'px',
        },
      });
    }

    return transformed;
  }

  _getStylesByElemId(
    selectedElems: IElement[],
    handlerType: ResizeHandlerPositions,
    deltaX: number,
    deltaY: number,
  ): IStylesByElemId[] {
    const result: IStylesByElemId[] = [];

    for (const selectedElem of selectedElems) {
      const { leftValue, topValue } = this._getTopLeftValuesByHandlerType(
        selectedElem,
        handlerType,
        deltaX,
        deltaY,
      );

      const updatedElemStyles = {
        left: leftValue + 'px',
        top: topValue + 'px',
        width: parseInt(selectedElem.styles.width) * (1 + deltaX) + 'px',
        height: parseInt(selectedElem.styles.height) * (1 + deltaY) + 'px',
      };

      const updatedChildrenStyles =
        selectedElem.elemType === ElemTypes.GROUP && selectedElem.children
          ? this._getChildrenStyles(selectedElem, updatedElemStyles)
          : null;

      result.push({
        id: selectedElem.id,
        styles: updatedElemStyles,
        children: updatedChildrenStyles,
      });
    }

    return result;
  }

  _getTopLeftValuesByHandlerType(
    item: IElement,
    handlerType: ResizeHandlerPositions,
    deltaX: number,
    deltaY: number,
  ): {
    leftValue: number;
    topValue: number;
  } {
    const initialSelectionPageX = parseInt(this.initialSelectionStyles.left);
    const initialSelectionPageY = parseInt(this.initialSelectionStyles.top);

    switch (handlerType) {
      case ResizeHandlerPositions.BOTTOM_RIGHT_CORNER:
      case ResizeHandlerPositions.BOTTOM_SIDE:
      case ResizeHandlerPositions.RIGHT_SIDE: {
        const insideLeftOffsetX =
          parseInt(item.styles.left) - initialSelectionPageX;
        const insideTopOffsetY =
          parseInt(item.styles.top) - initialSelectionPageY;

        return {
          leftValue: initialSelectionPageX + insideLeftOffsetX * (deltaX + 1),
          topValue: initialSelectionPageY + insideTopOffsetY * (deltaY + 1),
        };
      }
      case ResizeHandlerPositions.TOP_LEFT_CORNER:
      case ResizeHandlerPositions.TOP_SIDE:
      case ResizeHandlerPositions.LEFT_SIDE: {
        const initialSelectionRightCoord =
          initialSelectionPageX + parseInt(this.initialSelectionStyles.width);
        const initialSelectionBottomCoord =
          initialSelectionPageY + parseInt(this.initialSelectionStyles.height);

        const insideRightOffsetX =
          initialSelectionRightCoord -
          parseInt(item.styles.left) +
          parseInt(item.styles.width);
        const insideBottomOffsetY =
          initialSelectionBottomCoord -
          parseInt(item.styles.top) +
          parseInt(item.styles.height);

        return {
          leftValue:
            initialSelectionRightCoord -
            (insideRightOffsetX - parseInt(item.styles.width)) * (1 + deltaX),
          topValue:
            initialSelectionBottomCoord -
            (insideBottomOffsetY - parseInt(item.styles.height)) * (1 + deltaY),
        };
      }
      case ResizeHandlerPositions.BOTTOM_LEFT_CORNER: {
        const initialSelectionRightCoord =
          initialSelectionPageX + parseInt(this.initialSelectionStyles.width);

        const insideRightOffsetX =
          initialSelectionRightCoord -
          parseInt(item.styles.left) +
          parseInt(item.styles.width);
        const insideTopOffsetY =
          parseInt(item.styles.top) - initialSelectionPageY;

        return {
          leftValue:
            initialSelectionRightCoord -
            (insideRightOffsetX - parseInt(item.styles.width)) * (1 + deltaX),
          topValue: initialSelectionPageY + insideTopOffsetY * (deltaY + 1),
        };
      }
      case ResizeHandlerPositions.TOP_RIGHT_CORNER: {
        const initialSelectionBottomCoord =
          initialSelectionPageY + parseInt(this.initialSelectionStyles.height);

        const insideLeftOffsetX =
          parseInt(item.styles.left) - initialSelectionPageX;
        const insideBottomOffsetY =
          initialSelectionBottomCoord -
          parseInt(item.styles.top) +
          parseInt(item.styles.height);

        return {
          leftValue: initialSelectionPageX + insideLeftOffsetX * (deltaX + 1),
          topValue:
            initialSelectionBottomCoord -
            (insideBottomOffsetY - parseInt(item.styles.height)) * (1 + deltaY),
        };
      }
      default:
        return { leftValue: 0, topValue: 0 };
    }
  }

  _getChildrenStyles(
    groupElem: IElement,
    newGroupStyles: CSSProperties,
  ): IStylesByElemId[] | null {
    const initialGroupStyles = this.initialGroupElems.find(
      (item) => item.id === groupElem.id,
    )?.styles;

    if (!initialGroupStyles) return null;

    const initialSelectionWidth = parseInt(initialGroupStyles.width);
    const initialSelectionHeight = parseInt(initialGroupStyles.height);

    const diffX = parseInt(newGroupStyles.width) - initialSelectionWidth;
    const diffY = parseInt(newGroupStyles.height) - initialSelectionHeight;

    const deltaX = diffX / initialSelectionWidth;
    const deltaY = diffY / initialSelectionHeight;

    const newChildrenStyles: IStylesByElemId[] | null = groupElem.children
      ? this._transformChildren(groupElem.children, deltaX, deltaY)
      : null;

    return newChildrenStyles;
  }

  _changeNestedElemsStyles(handlerType: ResizeHandlerPositions): void {
    const newStyles = this.domElem.style;

    const initialSelectionWidth = parseInt(this.initialSelectionStyles.width);
    const initialSelectionHeight = parseInt(this.initialSelectionStyles.height);

    const diffX = parseInt(newStyles.width) - initialSelectionWidth;
    const diffY = parseInt(newStyles.height) - initialSelectionHeight;

    const deltaX = diffX / initialSelectionWidth;
    const deltaY = diffY / initialSelectionHeight;

    const newStylesByElemId: IStylesByElemId[] = this._getStylesByElemId(
      this.selectedElems,
      handlerType,
      deltaX,
      deltaY,
    );

    this.dispatch(changeEverySelectedElemByOwnStyles(newStylesByElemId));
  }

  sideHorizontalResize(
    handlerType: ResizeHandlerPositions,
    pageX: number,
    pageY: number,
  ): void {
    super.sideHorizontalResize(handlerType, pageX, pageY);

    this._changeNestedElemsStyles(handlerType);
  }

  sideVerticalResize(
    handlerType: ResizeHandlerPositions,
    pageX: number,
    pageY: number,
  ): void {
    super.sideVerticalResize(handlerType, pageX, pageY);

    this._changeNestedElemsStyles(handlerType);
  }

  cornerResize(
    handlerType: ResizeHandlerPositions,
    pageX: number,
    pageY: number,
    pressedKeys: string[],
  ): void {
    super.cornerResize(handlerType, pageX, pageY, pressedKeys);

    this._changeNestedElemsStyles(handlerType);
  }
}
